import type { Snapshot } from "./api";
import { vehicleNeedsCrew } from "./crewAssign";

/** Einsatzwachen — Besatzung gilt nur hier, nicht Leitstelle/Werkstatt/Schule. */
export const OPS_STATION_KINDS = [
  "fire_station",
  "police_station",
  "ems_station",
  "thw",
] as const;

export function isOpsStation(kind: string): boolean {
  return (OPS_STATION_KINDS as readonly string[]).includes(kind);
}

export function crewAssignBuildings(snap: Snapshot) {
  return snap.buildings.filter((b) => isOpsStation(b.kind));
}

export function stationHasCrewVehicles(snap: Snapshot, buildingId: string): boolean {
  return snap.vehicles.some((v) => v.buildingId === buildingId && (v.crew ?? 0) > 0);
}

export function stationUnderstaffedCount(snap: Snapshot, buildingId: string): number {
  const pers = snap.personnel as Array<{ vehicleId?: string | null }>;
  return snap.vehicles.filter(
    (v) => v.buildingId === buildingId && vehicleNeedsCrew(v, pers),
  ).length;
}

/** Unterbesetzte Fahrzeuge nur an Einsatzwachen — nicht Leitstelle o. Ä. */
export function opsUnderstaffedVehicleCount(snap: Snapshot): number {
  const opsIds = new Set(crewAssignBuildings(snap).map((b) => b.id));
  const pers = snap.personnel as Array<{ vehicleId?: string | null }>;
  return snap.vehicles.filter(
    (v) => opsIds.has(v.buildingId) && vehicleNeedsCrew(v, pers),
  ).length;
}

/** Bevorzugte Wache für Tab Besatzung — unterbesetzt > mit Fahrzeugen > erste Ops-Wache. */
export function preferredCrewAssignBuildingId(
  snap: Snapshot,
  selectedId?: string | null,
): string {
  const ops = crewAssignBuildings(snap);
  const understaffed = ops.find((b) => stationUnderstaffedCount(snap, b.id) > 0);
  if (understaffed) return understaffed.id;

  if (
    selectedId &&
    ops.some((b) => b.id === selectedId) &&
    stationHasCrewVehicles(snap, selectedId)
  ) {
    return selectedId;
  }

  const withVehicles = ops.find((b) => stationHasCrewVehicles(snap, b.id));
  if (withVehicles) return withVehicles.id;

  return ops[0]?.id ?? "";
}

/** Falsche Wache gewählt — z. B. Leitstelle oder leere Wache obwohl Ops-Wache Fahrzeuge hat. */
export function isWrongCrewAssignStation(
  snap: Snapshot,
  selectedBuildingId: string | null,
  assignBuildingId?: string | null,
): boolean {
  const opsWithCrew = crewAssignBuildings(snap).filter((b) =>
    stationHasCrewVehicles(snap, b.id),
  );
  if (opsWithCrew.length === 0) return false;

  const effectiveId = assignBuildingId || selectedBuildingId;
  if (!effectiveId) return true;

  const building = snap.buildings.find((b) => b.id === effectiveId);
  if (!building || !isOpsStation(building.kind)) return true;

  if (!stationHasCrewVehicles(snap, effectiveId)) {
    return opsWithCrew.length > 0;
  }

  return false;
}
